import { useEffect, useRef, useState } from 'react';

/* ────────────────────────────────────────────────────────────
 * useCountUp — animate a numeric value up to its target.
 *
 * MOTION.md §4.9:
 *   - 600ms with --ease-out-quart (cubic-bezier(0.25, 1, 0.5, 1)).
 *   - Decimal values count smoothly; integer values use a step
 *     function so fractional digits don't flicker.
 *   - Runs on mount and again whenever the target changes, so
 *     tiles fed by async data animate into their real number
 *     instead of latching on the placeholder they mounted with.
 *
 * Respects `prefers-reduced-motion` — snaps instantly to target.
 * ──────────────────────────────────────────────────────────── */

const EASE_OUT_QUART = (t) => 1 - Math.pow(1 - t, 4);

const useCountUp = (target, { duration = 600, decimals = 0 } = {}) => {
  const [value, setValue] = useState(0);
  // Holds the last frame we painted so a mid-flight target change
  // continues from where the numeral currently sits rather than
  // snapping back to zero.
  const currentRef = useRef(0);

  useEffect(() => {
    if (typeof target !== 'number' || Number.isNaN(target)) {
      currentRef.current = target;
      setValue(target);
      return undefined;
    }

    const prefersReduced =
      typeof window !== 'undefined' &&
      window.matchMedia &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    if (prefersReduced || duration <= 0) {
      currentRef.current = target;
      setValue(target);
      return undefined;
    }

    const from = typeof currentRef.current === 'number' && !Number.isNaN(currentRef.current)
      ? currentRef.current
      : 0;

    if (from === target) return undefined;

    let raf;
    const start = performance.now();
    const step = (now) => {
      const elapsed = now - start;
      const t = Math.min(1, elapsed / duration);
      const eased = EASE_OUT_QUART(t);
      const next = from + (target - from) * eased;
      const painted = decimals === 0 ? Math.round(next) : next;
      currentRef.current = painted;
      setValue(painted);
      if (t < 1) {
        raf = requestAnimationFrame(step);
      } else {
        currentRef.current = target;
        setValue(target);
      }
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [target, duration, decimals]);

  return value;
};

export default useCountUp;
